import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { SEOHead } from "@/components/seo/SEOHead";
import aiveliqLogo from "@/assets/aiveliq360-logo.png";

export default function PrivacyPolicy() {
  return (
    <>
      <SEOHead 
        title="Privacy Policy | Aiveliq360"
        description="Learn how Aiveliq360 collects, uses, and protects your personal data. Our privacy policy outlines your rights and our data handling practices."
        canonicalUrl="https://aiveliq360.com/privacy"
      />
      <div className="min-h-screen bg-background">
        <nav className="border-b border-border py-3">
          <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={aiveliqLogo} alt="Aiveliq360" className="h-6 object-contain" />
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm" className="gap-1.5">
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to Home
              </Button>
            </Link>
          </div>
        </nav>

        <main className="max-w-4xl mx-auto px-4 py-12">
          <h1 className="text-3xl font-bold mb-8">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: December 11, 2025</p>

          <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-xl font-semibold mb-4">1. Introduction</h2>
              <p className="text-muted-foreground mb-4">
                Aiveliq360 ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI Sales Operating System platform.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">2. Information We Collect</h2>
              <h3 className="text-lg font-medium mb-2">2.1 Personal Information</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Name, email address, and contact information</li>
                <li>Company name, job title, and business information</li>
                <li>Account credentials and authentication data</li>
                <li>Billing and payment information (processed by Stripe)</li>
              </ul>

              <h3 className="text-lg font-medium mb-2">2.2 Usage Information</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Log data including IP address, browser type, and device information</li>
                <li>Usage patterns and feature interactions</li>
                <li>Sales data and CRM information you input into the platform</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">3. How We Use Your Information</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>To provide and maintain our AI Sales Operating System services</li>
                <li>To process transactions and send related information</li>
                <li>To send administrative information and service updates</li>
                <li>To improve and personalize your experience</li>
                <li>To train and improve our AI models (using aggregated, anonymized data)</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">4. Data Sharing and Disclosure</h2>
              <p className="text-muted-foreground mb-4">We may share your information with:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Service Providers:</strong> Third-party vendors who assist in providing our services (e.g., cloud hosting, payment processing, analytics)</li>
                <li><strong>Business Partners:</strong> With your consent, for integrated services</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">5. Data Security</h2>
              <p className="text-muted-foreground">
                We implement industry-standard security measures including encryption at rest and in transit, secure authentication, and regular security audits. However, no method of transmission over the Internet is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">6. Your Rights (GDPR & CCPA)</h2>
              <p className="text-muted-foreground mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Access:</strong> Request copies of your personal data</li>
                <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Erasure:</strong> Request deletion of your personal data</li>
                <li><strong>Portability:</strong> Request transfer of your data</li>
                <li><strong>Object:</strong> Object to processing of your personal data</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">7. Cookies and Tracking</h2>
              <p className="text-muted-foreground">
                We use cookies and similar tracking technologies. For detailed information, please see our <Link to="/cookies" className="text-primary hover:underline">Cookie Policy</Link>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">8. Data Retention</h2>
              <p className="text-muted-foreground">
                We retain your personal data only for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required by law.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">9. International Transfers</h2>
              <p className="text-muted-foreground">
                Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers in compliance with applicable data protection laws.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">10. Contact Us</h2>
              <p className="text-muted-foreground">
                For questions about this Privacy Policy or to exercise your rights, contact us at:<br />
                <strong>Email:</strong> privacy@aiveliq360.com<br />
                <strong>Address:</strong> Aiveliq360, Privacy Team
              </p>
            </section>
          </div>
        </main>

        <footer className="border-t border-border py-8">
          <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <img src={aiveliqLogo} alt="Aiveliq360" className="h-6 object-contain" />
            <nav className="flex items-center gap-4 text-xs text-muted-foreground">
              <Link to="/privacy" className="text-foreground">Privacy</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
            </nav>
            <p className="text-xs text-muted-foreground">© 2025 Aiveliq360</p>
          </div>
        </footer>
      </div>
    </>
  );
}
